import { Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

const fallbackTestimonials = [
  {
    name: "Margaret T.",
    role: "Senior Patient",
    content: "ANEMT has been a lifesaver for my dialysis appointments. Always on time, always professional. I feel safe and cared for.",
    rating: 5,
  },
  {
    name: "Robert K.",
    role: "Senior Patient",
    content: "Finding reliable wheelchair transport for my mother was challenging until we found ANEMT. They treat her with dignity and respect every time.",
    rating: 5,
  },
  {
    name: "Susan L.",
    role: "Regular Client",
    content: "The drivers are courteous, the vehicles are clean, and the pricing is transparent. Highly recommend for anyone needing medical transport!",
    rating: 5,
  },
];

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGoogleReviews = async () => {
      try {
        const placeId = "ChIJTeBs55uhlVQRiVy8EwOhiIc";
        const { data, error } = await supabase.functions.invoke('fetch-google-reviews', {
          body: { placeId }
        });
        if (!error && data?.reviews?.length > 0) {
          setTestimonials(data.reviews.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching Google reviews:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGoogleReviews();
  }, []);

  return (
    <section className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground mb-3 sm:mb-4">
            What Our Clients Say
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Real reviews from the people we serve
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {isLoading
            ? [1, 2, 3].map((i) => (
                <Card key={i} className="border-border rounded-2xl shadow-sm animate-pulse">
                  <CardContent className="pt-5 sm:pt-6">
                    <div className="flex mb-3 sm:mb-4 gap-1">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div key={j} className="h-4 w-4 sm:h-5 sm:w-5 bg-muted rounded" />
                      ))}
                    </div>
                    <div className="space-y-2 mb-5 sm:mb-6">
                      <div className="h-4 bg-muted rounded w-full" />
                      <div className="h-4 bg-muted rounded w-5/6" />
                    </div>
                    <div className="h-4 bg-muted rounded w-1/3" />
                  </CardContent>
                </Card>
              ))
            : testimonials.map((testimonial, index) => (
                <Card key={index} className="border-border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                  <CardContent className="pt-5 sm:pt-6 px-4 sm:px-6">
                    <div className="flex mb-3 sm:mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 italic leading-relaxed">
                      "{testimonial.content}"
                    </p>
                    <div>
                      <p className="font-semibold text-foreground text-sm sm:text-base">{testimonial.name}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
